// La configuración de EJECUCIÓN de la WebApp pública: clave de Maps y direcciones de las API.
//
// La clave vivía en los cuatro `environment*.ts`, y los cuatro están commiteados: la clave
// quedaba en el historial del repo y en cada bundle. Una clave de navegador NO se puede ocultar —
// el navegador de quien escanea el QR tiene que recibirla —, así que la defensa real es la
// restricción por referrer en la consola de Google, no el secreto. Pero no tiene por qué
// vivir en git.
//
// Las direcciones de las API vivían fijadas en la compilación (`environment.apiURL`), y eso
// obligaba a una imagen por entorno: la misma imagen llamaba a producción desde cualquier host.
// Ahora son configuración de ejecución, igual que la clave.
//
// `assets/runtime-config.js` lo carga index.html ANTES de que arranque Angular y solo define
// una global. El entrypoint del contenedor escribe ese fichero a partir de GOOGLE_MAPS_API_KEY,
// API_URL, PASSPORT_API_URL y APP_URL al arrancar, así que la misma imagen sirve para cualquier
// entorno sin recompilar y sin que la clave se commitee nunca.
//
// Qué pasa cuando falta algo:
//  - la clave de Maps: ESTADO HONESTO, no un fallo. `GoogleMap.create` rechaza y los mapas ya
//    muestran su propio mensaje.
//  - una dirección: se usa la compilada en `environment` (la de siempre). En el contenedor esto
//    no ocurre nunca — el entrypoint escribe SIEMPRE las tres, con el defecto de la imagen si
//    nadie las pasó, y lo dice en el log —, pero en `ng serve` y en los tests sí, y ahí el
//    comportamiento es exactamente el de antes de este cambio.
import { environment } from '../../environments/environment'

export interface RuntimeConfig {
	readonly googleMapsApiKey: string
	readonly apiUrl: string
	readonly passportApiUrl: string
	readonly appUrl: string
}

interface RuntimeConfigWindow extends Window {
	readonly __STELVIO_RUNTIME_CONFIG__?: Partial<RuntimeConfig>
}

function textOf(value: unknown): string {
	return typeof value === 'string' ? value.trim() : ''
}

// Sin barras finales: los consumidores concatenan `${base}/ruta`, y `base/` daría `//ruta`.
export function withoutTrailingSlash(value: string): string {
	return value.replace(/\/+$/, '')
}

// Pura, y por eso testeable: normaliza lo que haya puesto el entrypoint, que es texto
// generado fuera de este código y puede llegar de cualquier forma.
export function readRuntimeConfig(source: Partial<RuntimeConfig> | undefined | null): RuntimeConfig {
	const raw = source === null || source === undefined ? {} : source
	return {
		googleMapsApiKey: textOf(raw.googleMapsApiKey),
		apiUrl: withoutTrailingSlash(textOf(raw.apiUrl)),
		passportApiUrl: withoutTrailingSlash(textOf(raw.passportApiUrl)),
		appUrl: withoutTrailingSlash(textOf(raw.appUrl)),
	}
}

// Lo configurado gana; si no hay nada, la dirección compilada. Pura.
export function resolveEndpoint(configured: string, compiled: string): string {
	return configured !== '' ? configured : withoutTrailingSlash(compiled)
}

export function runtimeConfig(): RuntimeConfig {
	if (typeof window === 'undefined') return readRuntimeConfig(undefined)
	return readRuntimeConfig((window as RuntimeConfigWindow).__STELVIO_RUNTIME_CONFIG__)
}

export function googleMapsApiKey(): string {
	return runtimeConfig().googleMapsApiKey
}

export function hasGoogleMapsApiKey(): boolean {
	return googleMapsApiKey() !== ''
}

// Base de la API principal (tracking, wallet). Sin barra final.
export function apiUrl(): string {
	return resolveEndpoint(runtimeConfig().apiUrl, environment.apiURL)
}

// Base del servicio de pasaporte (`/passport/public/{id}`). Sin barra final.
export function passportApiUrl(): string {
	return resolveEndpoint(runtimeConfig().passportApiUrl, environment.passportApiURL)
}

// Origen público de la aplicación, del que cuelgan `/images/...`. Sin barra final.
export function appUrl(): string {
	return resolveEndpoint(runtimeConfig().appUrl, environment.appURL)
}
